import {createViewConfig} from '@sail/engine';
import type {View} from '@sail/engine';
import type {
  ListCollectionComponent,
  MenuTriggerProps,
  Expandable,
  Filterable,
} from '@sail/react-aria';
import type {Key} from '@react-types/shared';
import type {SelectionProps} from './SelectField';
import type {FormFieldProps} from './FormField';
import type {FauxTreeItemProps, FauxTreeItemTransforms} from './FauxTreeItem';
import type {
  FauxTreeGroupProps,
  FauxTreeGroupTransforms,
} from './FauxTreeGroup';
import type {Popover} from './popover/Popover';
import type {FauxTreeProps} from './FauxTree';
import type {Icon} from './Icon';
import type {Action} from './Action';
import type {NativeTextField, TextField} from './TextField';

type MessagesConfig = {
  selectedText?: (t: number) => string;
  numberOfItemsText?: (t: number) => string;
  andMoreText?: (t: number) => string;
};

export type SearchFieldProps<T> = FormFieldProps<
  ListCollectionComponent<
    T,
    FauxTreeItemProps,
    typeof FauxTreeItemTransforms.defaultField,
    FauxTreeGroupProps,
    typeof FauxTreeGroupTransforms.idField
  > &
    Expandable &
    SelectionProps &
    Omit<Filterable<T>, 'filterable'> &
    MenuTriggerProps & {
      /** Controls whether the control should be automatically focused */
      autoFocus?: boolean;
      /** Text that appears in the input when it has no value */
      placeholder?: string;
      /** Handler that is called when the SearchField is submitted. */
      onSubmit?: (value: string, results: Set<string>, focusedKey: Key) => void;
      /** The current value (controlled). */
      value?: string;
      /** The default value (uncontrolled). */
      defaultValue?: string;
      /** Handler that is called when the input value changes. */
      onInputChange?: (value: string) => void;
      /** Whether the input is disabled. */
      disabled?: boolean;
      /** The disabled keys in the search results. */
      disabledKeys?: Iterable<Key>;
      /** Whether the input can be selected but not changed by the user. */
      readOnly?: boolean;
      /** Whether the value is required */
      required?: boolean;
      /**
       * The interaction required to trigger the display of search results.
       * @default 'input'
       */
      menuTrigger?: 'focus' | 'input' | 'manual';
      /**
       * Whether or not the list can be empty. If true, the list will always
       * show the empty state when there are no results.
       */
      allowsEmptyCollection?: boolean;
      /**
       * Content to render when the list is empty.
       */
      emptyState?: React.ReactNode;
      /**
       * Whether the SearchField should close on blur.
       */
      shouldCloseOnBlur?: boolean;
      /**
       * Controls the height of the input.
       *
       * @default 'medium'
       */
      size?: 'small' | 'medium' | 'large';

      subviews?: View.Subviews<{
        popover: typeof Popover;
        listbox: FauxTreeProps<unknown>;
        searchIcon: typeof Icon;
        cancelAction: typeof Action;
        textfield: typeof TextField;
      }>;
      /** Messages used to translate texts inside of the search field.
       * selectedText: -> "X of Y selected"
       * numberOfItemsText: -> "X items"
       * andMoreText: -> "and X more"
       */
      messages?: MessagesConfig;
      /**  Set to false to hide the option to select/unselect all items */
      selectionAllEnabled?: boolean;
      /**
       * Set to false to hide the option to select/unselect all items in a group.
       */
      groupSelectionAllEnabled?: boolean;
      /**
       * @private
       * If defined, the list will have a clear selection option on top of the regular items.
       */
      _SAIL_INTERNAL_USE_clearable?: {
        label: string;
      };
    },
  typeof NativeTextField
>;

export const SearchFieldConfig = createViewConfig({
  name: 'SearchField',
  props: {} as SearchFieldProps<unknown>,
  defaults: {
    defaultExpandedKeys: 'all',
    expandable: false,
    selectionMode: 'single',
  },
});
